import React, { useContext, useEffect, useMemo, useState } from 'react'
import { ServicesContext, ServicesObjectType } from '../../../../contexts/ServicesContext'
import ModalContext from '../../../../contexts/ModalContext'
import { OccurrenceContext } from '../../../../contexts/OccurrenceContext'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { ProductionOutletType } from '..'
import { Filter } from '../../../../components/SubHeaderFilter/types'
import axios from '../../../../config/axios'
import { AlertTriangle, ArrowLeftRight, Download, Eye, FileText, Pencil, Trash2 } from 'lucide-react'
import { TableContainer } from '../style'
import { StandardDataTable, useColumnsState } from '../../../../components/StandardPage'
import { buildProductionOrderColumns, productionOrdersScreenKey } from './productionOrderColumns'
import GeneratePDFModal from '../../../../components/Modals/ViewPCP/GeneratePDFModal'
import GenerateInspectionFormModal from '../../../../components/Modals/ViewPCP/GenerateInspectionForm'
import TransferModal from '../../../../components/Modals/ViewPCP/TransferServices'

const ServicesTable2: React.FC = () => {
  const { setModalSection, setModalActive } = useContext(ModalContext)
  const { setService } = useContext(ServicesContext)
  const { setOccurrence } = useContext(OccurrenceContext)

  const [servicesList, setServicesList] = useState<ServicesObjectType[]>([])
  const [totalServices, setTotalServices] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [hasNextPage, setHasNextPage] = useState<boolean>(false)
  const [subModal, setSubModal] = useState<'transfer' | 'pdf' | 'inspection' | null>(null)
  const [subModalServiceId, setSubModalServiceId] = useState<number | null>(null)

  const { search, appliedFilters, refresh } = useOutletContext<ProductionOutletType>()
  const navigate = useNavigate()

  const columns = useMemo(() => buildProductionOrderColumns(), [])
  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(
    productionOrdersScreenKey,
    columns,
  )

  const ITEMS_PER_PAGE = 50
  const fetchServices = async (page: number = 1, searchTerm: string = '', filters: Filter[] = [], append: boolean = false) => {
    setIsLoading(true)

    try {
      const params: any = {
        page,
        limit: ITEMS_PER_PAGE
      }

      if (searchTerm && searchTerm.trim() !== '') {
        params.search = searchTerm.trim()
      }

      if (filters.length > 0) {
        params.filters = JSON.stringify(filters)
      }

      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/pcp/services/paginated`, {
        params
      })

      const data = response.data

      if (append) {
        setServicesList(prev => [...prev, ...(data.data || [])])
      } else {
        setServicesList(data.data || [])
      }

      setTotalServices(data.total || 0)
      setCurrentPage(page)
      setHasNextPage((data.data?.length || 0) === ITEMS_PER_PAGE && (page * ITEMS_PER_PAGE) < (data.total || 0))

    } catch (err: any) {
      if (!append) {
        setServicesList([])
        setTotalServices(0)
        setHasNextPage(false)
      }
    } finally {
      setIsLoading(false)
    }
  }
  const [debouncedSearch, setDebouncedSearch] = useState<string>('')
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search || '')
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
    setServicesList([])
    fetchServices(1, debouncedSearch, appliedFilters || [], false)
  }, [debouncedSearch, appliedFilters, refresh])

  const handleLoadMore = () => {
    if (!isLoading && hasNextPage) {
      const nextPage = currentPage + 1
      fetchServices(nextPage, debouncedSearch, appliedFilters || [], true)
    }
  }

  const handleViewService = (service: ServicesObjectType) => {
    setService(service)
    setModalSection('viewOPServiceModal')
    setModalActive(true)
  }

  const handleEditService = (service: ServicesObjectType) => {
    navigate(`/panel/production/edit/${service.id}`, { state: { returnSearch: search } })
  }

  const handleGenerateOccurrence = (service: ServicesObjectType) => {
    setOccurrence({
      serviceId: service.id,
      operationNumber: (service as any).codeOfOP || '',
    } as any)
    setModalSection('createEditOccurrenceModal')
    setModalActive(true)
  }

  const handleOpenSubModal = (service: ServicesObjectType, section: 'transfer' | 'pdf' | 'inspection') => {
    if (!service?.id) return
    navigate(`${window.location.pathname}?id=${service.id}`, { replace: true })
    setSubModalServiceId(Number(service.id))
    setSubModal(section)
  }

  const handleCloseSubModal = () => {
    navigate(window.location.pathname, { replace: true })
    setSubModal(null)
    setSubModalServiceId(null)
  }

  const handleDeleteService = async (service: ServicesObjectType) => {
    if (window.confirm(`Tem certeza que deseja excluir ${(service as any).codeOfOP}?`)) {
      try {
        await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/${service.id}`)
        fetchServices(1, debouncedSearch, appliedFilters || [], false)
      } catch (error) {
        console.error('Erro ao excluir serviço:', error)
        alert('Erro ao excluir serviço')
      }
    }
  }

  return (
    <TableContainer>
      <StandardDataTable
        tableName="Services"
        columns={columns}
        data={servicesList}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        totalCount={totalServices}
        onLoadMore={handleLoadMore}
        hasMore={hasNextPage}
        isLoading={isLoading}
        countLabel='ordens de produção'
        exportFileName='ordens-de-producao'
        rowActions={(service: any) => [
          { label: 'Visualizar', icon: <Eye size={16} />, onClick: () => handleViewService(service) },
          { label: 'Editar', icon: <Pencil size={16} />, onClick: () => handleEditService(service) },
          { label: 'Transferir', icon: <ArrowLeftRight size={16} />, onClick: () => handleOpenSubModal(service, 'transfer') },
          { label: 'Gerar ficha', icon: <FileText size={16} />, onClick: () => handleOpenSubModal(service, 'inspection') },
          { label: 'Gerar PDF', icon: <Download size={16} />, onClick: () => handleOpenSubModal(service, 'pdf') },
          { label: 'Gerar ocorrência', icon: <AlertTriangle size={16} />, onClick: () => handleGenerateOccurrence(service) },
          { label: 'Excluir', icon: <Trash2 size={16} />, onClick: () => handleDeleteService(service) },
        ]}
      />
      {subModal === 'transfer' && subModalServiceId && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 9500 }}>
          <TransferModal id={subModalServiceId} onClickClose={handleCloseSubModal} />
        </div>
      )}
      {subModal === 'pdf' && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 9500 }}>
          <GeneratePDFModal onClickClose={handleCloseSubModal} />
        </div>
      )}
      {subModal === 'inspection' && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 9500 }}>
          <GenerateInspectionFormModal onClickClose={handleCloseSubModal} />
        </div>
      )}
    </TableContainer>
  )
}

export default ServicesTable2;
